import React from 'react';
import PropTypes from 'prop-types';
import { Card, Image } from 'react-bootstrap';

import { getPlatformApexOrigin } from '@/shared/utils/ecosystemRouting';

/**
 * IdentitySummary: read-only view of the user's PLATFORM identity, shown on
 * the ecosystem (tenant) profile page.
 *
 * The tenant profile page previously rendered a second, fully editable copy of
 * the identity form (IdentityCard). Two editors for one fact is the hazard the
 * data-model work calls out: a user editing their bio here had no signal that
 * they were writing to a record shared across every ecosystem they belong to.
 * This block displays that identity and routes edits to the one page that owns
 * it, the platform Account page.
 *
 * The "edit" affordance is a REAL ANCHOR to an absolute apex URL, not a React
 * Router <Link>. This component only ever renders inside a tenant, where the
 * router basename would rewrite a relative `/account` into `/<slug>/account`.
 * That URL redirects to the apex, so a <Link> would still arrive, but only
 * after an extra hop and a brief mount of AccountPage under the wrong tenancy
 * level. Crossing tenancy levels is a full-page navigation, the same rule
 * YourEcosystems and the NavBar links follow.
 */
export default function IdentitySummary({ profile }) {
    const first = profile?.name?.first || '';
    const last = profile?.name?.last || '';
    const fullName = `${first} ${last}`.trim();

    // Treat whitespace-only as empty: a bio of spaces should surface the
    // prompt, not an invisible paragraph.
    const bio = profile?.bio?.trim();

    // Absolute apex URL, resolved at render time from the current host so it
    // works in path mode, subdomain mode, and on a custom tenant domain.
    const accountUrl = `${getPlatformApexOrigin()}/account`;

    return (
        <Card className="shadow-sm" id="identity">
            <Card.Header className="bg-white border-bottom">
                <strong>My RILE Connect Profile</strong>
                <div className="text-muted small mt-1">
                    Your name, photo, and bio are shared across every ecosystem
                    you belong to, and are managed in your RILE Connect account.
                </div>
            </Card.Header>
            <Card.Body className="p-4">
                <div className="d-flex align-items-center gap-3">
                    {profile?.profilePicture ? (
                        <Image
                            src={profile.profilePicture}
                            alt={fullName || 'Profile photo'}
                            roundedCircle
                            className="identity-summary-avatar"
                        />
                    ) : (
                        // Initial stands in rather than a broken <img>. Bootstrap
                        // utilities size and centre it; the sizing itself needs a
                        // class because Bootstrap has no fixed-px helper.
                        <div className="rounded-circle bg-light d-flex align-items-center justify-content-center identity-summary-avatar">
                            <span className="text-muted fs-4">
                                {first.charAt(0)}
                            </span>
                        </div>
                    )}

                    <div className="flex-grow-1">
                        {fullName && <div className="fw-bold">{fullName}</div>}
                        {profile?.email && (
                            <div className="text-muted small">
                                {profile.email}
                            </div>
                        )}
                        {bio ? (
                            <p className="mb-0 mt-2">{bio}</p>
                        ) : (
                            <p className="mb-0 mt-2 text-muted small">
                                No bio yet.
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-3">
                    <a href={accountUrl} className="small">
                        {bio
                            ? 'Edit in your RILE Connect account →'
                            : 'Set your RILE Connect bio →'}
                    </a>
                </div>
            </Card.Body>
        </Card>
    );
}

IdentitySummary.propTypes = {
    profile: PropTypes.shape({
        name: PropTypes.shape({
            first: PropTypes.string,
            last: PropTypes.string,
        }),
        bio: PropTypes.string,
        email: PropTypes.string,
        profilePicture: PropTypes.string,
    }),
};
